import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { useEffect } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { PRODUCTS } from '@/constants/products.constants';

import { PRODUCT_TYPE_DEFAULTS } from './ProductEditDialog.constants';
import { useProductDialog } from './ProductEditDialog.hooks';
import type { ProductEditDialogForm } from './ProductEditDialog.types';

export const ProductEditDialogContentEdit = () => {
  const {
    register,
    control,
    formState: { errors },
    resetField,
    setValue
  } = useFormContext<ProductEditDialogForm>();
  const { product } = useProductDialog();
  const productType =
    useWatch({ control, name: 'product_type' }) || PRODUCTS.LOG;

  // set the default value for subscription_unit based on the product type
  // when the type is switched back to the product's original type, restore the original values instead
  useEffect(() => {
    if (productType === product.product_type) {
      resetField('subscription_unit');
      resetField('product_name');
      resetField('code');
      resetField('retention');
      resetField('archive_retention');
      resetField('subscription_limit');
      return;
    }
    if (PRODUCT_TYPE_DEFAULTS[productType]) {
      setValue('subscription_unit', PRODUCT_TYPE_DEFAULTS[productType].unit);
      setValue('product_name', PRODUCT_TYPE_DEFAULTS[productType].name);
      setValue('code', PRODUCT_TYPE_DEFAULTS[productType].code);
      setValue('retention', '');
      setValue('archive_retention', '');
      if (productType === PRODUCTS.RET) {
        setValue('subscription_limit', 0);
        setValue('retention', '90');
        setValue('archive_retention', '365');
      }
    }
  }, [productType]);

  return (
    <Grid container spacing={2} sx={{ py: 1 }}>
      <Grid size={{ xs: 6 }}>
        <InputLabel id="product_name">Product Name</InputLabel>
        <FormControl fullWidth>
          <TextField
            variant="outlined"
            {...register('product_name', {
              required: 'Product name is required'
            })}
            error={!!errors.product_name}
            helperText={errors.product_name?.message}
          />
        </FormControl>
      </Grid>

      <Grid size={{ xs: 6 }}>
        <InputLabel id="productName">Product Type</InputLabel>
        <FormControl fullWidth>
          <Controller
            control={control}
            name="product_type"
            rules={{
              required: true
            }}
            render={({ field }) => (
              <Select
                {...field}
                error={!!errors.product_type}
                name="product_type"
              >
                {Object.values(PRODUCTS).map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            )}
          />
        </FormControl>
      </Grid>

      <Grid size={{ xs: 6 }}>
        <InputLabel id="exampleRequired">Start Date</InputLabel>
        <FormControl fullWidth>
          <Controller
            control={control}
            name="start_date"
            rules={{
              required: true
            }}
            render={({ field, fieldState: { error } }) => (
              <DatePicker
                slotProps={{
                  textField: {
                    error: !!error
                  }
                }}
                {...field}
                inputRef={field.ref}
                onAccept={field.onChange}
              />
            )}
          />
        </FormControl>
      </Grid>

      <Grid size={{ xs: 6 }}>
        <InputLabel id="exampleRequired">End Date</InputLabel>
        <FormControl fullWidth>
          <Controller
            control={control}
            name="end_date"
            rules={{
              required: true
            }}
            render={({ field, fieldState: { error } }) => (
              <DatePicker
                slotProps={{
                  textField: {
                    error: !!error
                  }
                }}
                {...field}
                inputRef={field.ref}
                onAccept={field.onChange}
              />
            )}
          />
        </FormControl>
      </Grid>

      <Grid size={{ xs: 6 }}>
        <InputLabel id="subscription_limit">Subscription Limit</InputLabel>
        <FormControl fullWidth>
          <TextField
            variant="outlined"
            error={!!errors.subscription_limit}
            {...register('subscription_limit', { required: true })}
          />
        </FormControl>
      </Grid>

      {productType !== PRODUCTS.RET && (
        <Grid size={{ xs: 6 }}>
          <InputLabel id="productName">Subscription Unit</InputLabel>
          <FormControl fullWidth>
            <TextField
              variant="outlined"
              error={!!errors.subscription_limit}
              {...register('subscription_unit')}
              disabled
            />
          </FormControl>
        </Grid>
      )}

      {productType === PRODUCTS.RET && (
        <>
          <Grid size={{ xs: 3 }}>
            <InputLabel id="retention">Retention</InputLabel>
            <FormControl fullWidth>
              <TextField variant="outlined" {...register('retention')} />
            </FormControl>
          </Grid>

          <Grid size={{ xs: 3 }}>
            <InputLabel id="archive_retention">Archive Retention</InputLabel>
            <FormControl fullWidth>
              <TextField
                variant="outlined"
                {...register('archive_retention')}
              />
            </FormControl>
          </Grid>
        </>
      )}
    </Grid>
  );
};

export default ProductEditDialogContentEdit;
